import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { postMerchantEmitTokenAPI } from 'src/serverApi/merchants';
import useWithAuthorities from 'src/utils/useWithAuthorities';
import permissions from 'src/constants/permissions';
import { Alert } from '@1c/ui';
import { copyToClipboard } from 'src/utils/copyToClipboard';
import Button from 'src/components/Button';
import LinkButton from 'src/components/LinkButton';
import Modal from 'src/components/Modal';
import Text from 'src/components/Text';
import MerchantInfo from './MerchantInfo';
import styles from './styles.module.scss';
import { toast } from 'react-toastify';

type Props = {
  merchant: any;
  reloadMerchants: () => Promise<any>;
};

const ReissueTokenButton: React.FC<Props> = ({ merchant, reloadMerchants }) => {
  const { organizationUeid = '', bankUeid = '' } = useParams();
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);
  const [isEmitDialogOpen, setIsEmitDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const withAuthorities = useWithAuthorities([permissions.dataManagement]);

  if (!merchant) {
    return null;
  }

  const handleReissueTokenButtonClick = () => {
    setIsRevokeDialogOpen(true);
  };

  const handleCloseRevokeDialog = () => {
    setIsRevokeDialogOpen(false);
  };

  const handleReissueTokenAccept = async () => {
    try {
      setIsLoading(true);
      await withAuthorities(async () => {
        const token = await postMerchantEmitTokenAPI(
          organizationUeid,
          bankUeid,
          merchant?.ueid,
          merchant.alternativeAccountNumber
            ? { alternativeAccountNumber: merchant.alternativeAccountNumber }
            : {}
        );
        setToken(token);
        setIsRevokeDialogOpen(false);
        setIsEmitDialogOpen(true);
      });
    } catch (error) {
      toast.error('Ошибка при попытке перевыпустить токен');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (token) {
      copyToClipboard(token)
        .then(() => toast.success('Токен скопирован в буфер'))
        .catch(() => toast.error('Ошибка при копировании токена в буфер'));
    }
  };

  const handleCloseEmitDialog = () => {
    setIsEmitDialogOpen(false);
    setToken(null);
    // список мерчантов перезапрашиваем на закрытии диалога просмотра токена, а не в handleReissueTokenAccept,
    // потому что при обновлении списка обновляется таблица мерчантов и ячейки действий,
    // таким образом сбрасывается стейт ячеек (они по факту отрисовываются с нуля) и все диалоги закрываются
    // т.е. если обновить список мерчантов в handleReissueTokenAccept, то диалог выпуска токена запроется
    // в рамках обработки хендлера (фактически он вообще не откроется)
    reloadMerchants();
  };

  return (
    <>
      <LinkButton onClick={handleReissueTokenButtonClick}>Перевыпустить</LinkButton>
      <Modal
        isOpen={isRevokeDialogOpen}
        onClose={handleCloseRevokeDialog}
        title="Вы действительно хотите перевыпустить токен?"
        content={
          <>
            <Alert level="danger" className={styles['reissue-alert']}>
              Токен, который был выпущен раньше, перестанет действовать. Вместо него будет выпущен
              новый.
            </Alert>
            <MerchantInfo
              merchant={merchant}
              tokenEmissionTimestamp={merchant?.tokenEmissionTimestamp}
            />
          </>
        }
        footer={
          <>
            <Button
              disabled={isLoading}
              onClick={handleCloseRevokeDialog}
              className={styles['modal-button']}
            >
              Отмена
            </Button>
            <Button
              variant="outlined"
              disabled={isLoading}
              onClick={handleReissueTokenAccept}
              className={styles['modal-button']}
            >
              Перевыпустить
            </Button>
          </>
        }
      />
      <Modal
        isOpen={isEmitDialogOpen}
        onClose={handleCloseEmitDialog}
        title="Токен получен"
        content={
          <>
            <MerchantInfo merchant={merchant} />
            <Alert level="info" className={styles['token-alert']}>
              <Text type="h3" className={styles['token-title']}>
                Токен
              </Text>
              <Text
                type="p"
                className={styles['token-body']}
                lang="en"
                translate="no"
                spellCheck={false}
              >
                {token}
              </Text>
            </Alert>
            <Text type="p" muted italic className={styles['token-instruction']}>
              Скопируйте токен и вставьте его в форму настройки СБП в программе. После закрытия окна
              получить повторно этот токен будет невозможно. Потребуется перевыпуск нового токена
            </Text>
          </>
        }
        footer={
          <>
            <Button onClick={handleCopyToken} className={styles['modal-button']}>
              Копировать
            </Button>
            <Button
              variant="outlined"
              onClick={handleCloseEmitDialog}
              className={styles['modal-button']}
            >
              Закрыть
            </Button>
          </>
        }
      />
    </>
  );
};

export default ReissueTokenButton;
